import React, { useState, useMemo } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Chip, Grid, Card, CardActionArea,
  CardContent, Accordion, AccordionSummary, AccordionDetails, Paper, Stack,
  Divider, Alert, Tooltip, IconButton, LinearProgress, Badge, Avatar, Modal, Fade,
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AddCardIcon from '@mui/icons-material/AddCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CalculateIcon from '@mui/icons-material/Calculate';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import AddBoxIcon from '@mui/icons-material/AddBox';
import TimelineIcon from '@mui/icons-material/Timeline';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TransformIcon from '@mui/icons-material/Transform';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import HelpIcon from '@mui/icons-material/Help';
import PostAddIcon from '@mui/icons-material/PostAdd';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BalanceIcon from '@mui/icons-material/Balance';
import LockIcon from '@mui/icons-material/Lock';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditIcon from '@mui/icons-material/Edit';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentsIcon from '@mui/icons-material/Payments';
import KeyIcon from '@mui/icons-material/Key';
import BusinessIcon from '@mui/icons-material/Business';

import MODULOS, { CATEGORIAS, MANUAL_VERSION, MANUAL_FECHA } from '../data/manualContent';

// ─── Mapa icono → componente ──────────────────────────────────
const ICON_MAP = {
  Inventory2: Inventory2Icon, RequestQuote: RequestQuoteIcon, Receipt: ReceiptIcon,
  ReceiptLong: ReceiptLongIcon, CreditScore: CreditScoreIcon, AddCard: AddCardIcon,
  LocalShipping: LocalShippingIcon, Cancel: CancelIcon, ShoppingCart: ShoppingCartIcon,
  AccountBalance: AccountBalanceIcon, LocalAtm: LocalAtmIcon, Calculate: CalculateIcon,
  MenuBook: MenuBookOutlinedIcon, Groups: GroupsIcon, ManageAccounts: ManageAccountsIcon,
  Settings: SettingsIcon, AddBox: AddBoxIcon, Timeline: TimelineIcon,
  NoteAdd: NoteAddIcon, Transform: TransformIcon, PointOfSale: PointOfSaleIcon,
  UploadFile: UploadFileIcon, Assignment: AssignmentIcon, ConfirmationNumber: ConfirmationNumberIcon,
  CloudSync: CloudSyncIcon, Help: HelpIcon, PostAdd: PostAddIcon,
  Warehouse: WarehouseIcon, DirectionsBus: DirectionsBusIcon, RemoveCircle: RemoveCircleIcon,
  AddShoppingCart: AddShoppingCartIcon, TrendingUp: TrendingUpIcon, TrendingDown: TrendingDownIcon,
  ArrowDownward: ArrowDownwardIcon, ArrowUpward: ArrowUpwardIcon, Balance: BalanceIcon,
  Lock: LockIcon, AccountTree: AccountTreeIcon, Edit: EditIcon, DateRange: DateRangeIcon,
  FileDownload: FileDownloadIcon, PersonAdd: PersonAddIcon, Payments: PaymentsIcon,
  Key: KeyIcon, Business: BusinessIcon,
};
const Ico = ({ name, sx }) => { const C = ICON_MAP[name] || MenuBookIcon; return <C sx={sx} />; };

// ─── Helpers ──────────────────────────────────────────────────
const totalPasos = (modulo) => modulo.secciones.reduce((a, s) => a + s.pasos.length, 0);

const matchSearch = (modulo, q) => {
  if (!q) return true;
  const lower = q.toLowerCase();
  if (modulo.titulo.toLowerCase().includes(lower)) return true;
  if (modulo.descripcion.toLowerCase().includes(lower)) return true;
  return modulo.secciones.some(s =>
    s.titulo.toLowerCase().includes(lower) ||
    s.pasos.some(p =>
      p.titulo.toLowerCase().includes(lower) ||
      (Array.isArray(p.detalle) ? p.detalle.join(' ') : p.detalle).toLowerCase().includes(lower)
    )
  );
};

// ─── Captura de pantalla del paso, con zoom al hacer clic ─────
function CapturaPaso({ src, alt }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <Box
        onClick={() => setAbierto(true)}
        sx={{
          mt: 1.5, mb: 0.5, position: 'relative', display: 'inline-block',
          cursor: 'zoom-in', borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', maxWidth: 420,
          '&:hover .zoom-overlay': { opacity: 1 },
        }}
      >
        <Box component="img" src={src} alt={alt} loading="lazy"
          sx={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover', objectPosition: 'top' }} />
        <Box className="zoom-overlay" sx={{
          position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,.35)', opacity: 0,
          transition: 'opacity .15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ZoomInIcon sx={{ color: 'white', fontSize: 32 }} />
        </Box>
      </Box>

      <Modal open={abierto} onClose={() => setAbierto(false)} closeAfterTransition
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Fade in={abierto}>
          <Box sx={{ position: 'relative', maxWidth: '95vw', maxHeight: '92vh', outline: 'none' }}>
            <IconButton onClick={() => setAbierto(false)}
              sx={{ position: 'absolute', top: -44, right: 0, color: 'white', bgcolor: 'rgba(255,255,255,.15)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}>
              <CloseIcon />
            </IconButton>
            <Box component="img" src={src} alt={alt}
              sx={{ maxWidth: '95vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 1, boxShadow: 8 }} />
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

// ─── Paso individual ─────────────────────────────────────────
function PasoItem({ paso, colorHex }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
      {/* Número */}
      <Box sx={{
        minWidth: 36, height: 36, borderRadius: '50%', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        bgcolor: colorHex, color: 'white', fontWeight: 800, fontSize: 15,
        boxShadow: `0 2px 8px ${colorHex}55`,
      }}>
        {paso.num}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          {paso.titulo}
        </Typography>

        {/* Detalle */}
        {Array.isArray(paso.detalle) ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.3 } }}>
            {paso.detalle.map((d, i) => (
              <Typography component="li" key={i} variant="body2" color="text.secondary">{d}</Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">{paso.detalle}</Typography>
        )}

        {/* Captura(s) de pantalla del paso */}
        {paso.imagen && (
          <CapturaPaso src={paso.imagen} alt={paso.titulo} />
        )}
        {Array.isArray(paso.imagenes) && (
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {paso.imagenes.map((src, i) => (
              <CapturaPaso key={i} src={src} alt={`${paso.titulo} ${i + 1}`} />
            ))}
          </Stack>
        )}

        {/* Callouts */}
        {paso.tip && (
          <Alert severity="success" icon={<TipsAndUpdatesIcon fontSize="small" />}
            sx={{ mt: 1, py: 0.3, '& .MuiAlert-message': { fontSize: 13 } }}>
            <strong>Consejo:</strong> {paso.tip}
          </Alert>
        )}
        {paso.nota && (
          <Alert severity="info" icon={<InfoIcon fontSize="small" />}
            sx={{ mt: 1, py: 0.3, '& .MuiAlert-message': { fontSize: 13 } }}>
            {paso.nota}
          </Alert>
        )}
        {paso.alerta && (
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />}
            sx={{ mt: 1, py: 0.3, '& .MuiAlert-message': { fontSize: 13 } }}>
            <strong>Atención:</strong> {paso.alerta}
          </Alert>
        )}
        {paso.peligro && (
          <Alert severity="error" icon={<ErrorIcon fontSize="small" />}
            sx={{ mt: 1, py: 0.3, '& .MuiAlert-message': { fontSize: 13 } }}>
            <strong>Importante:</strong> {paso.peligro}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

// ─── Vista de detalle de módulo ──────────────────────────────
function ModuloDetalle({ modulo, onBack }) {
  const [expandedSec, setExpandedSec] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{
        background: `linear-gradient(135deg, ${modulo.colorHex}ee 0%, ${modulo.colorHex}99 100%)`,
        borderRadius: 3, p: 3, mb: 3, color: 'white',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Tooltip title="Volver al índice">
            <IconButton onClick={onBack} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,.15)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,.2)', borderRadius: 2, display: 'flex' }}>
            <Ico name={modulo.icono} sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800}>{modulo.titulo}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{modulo.descripcion}</Typography>
          </Box>
        </Box>

        {/* Stats del módulo */}
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip label={`${modulo.secciones.length} secciones`} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,.2)', color: 'white', fontWeight: 600 }} />
          <Chip label={`${totalPasos(modulo)} pasos`} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,.2)', color: 'white', fontWeight: 600 }} />
          <Chip label={modulo.categoria.toUpperCase()} size="small"
            sx={{ bgcolor: 'rgba(255,255,255,.2)', color: 'white', fontWeight: 600 }} />
        </Stack>
      </Paper>

      {/* Secciones */}
      {modulo.secciones.map((seccion, si) => (
        <Accordion key={si} expanded={expandedSec === si}
          onChange={() => setExpandedSec(expandedSec === si ? -1 : si)}
          elevation={0}
          sx={{ mb: 1.5, border: '1px solid', borderColor: expandedSec === si ? modulo.colorHex : 'divider',
                borderRadius: '12px !important', overflow: 'hidden',
                '&:before': { display: 'none' }, transition: 'border-color .2s' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: expandedSec === si ? `${modulo.colorHex}11` : 'background.paper',
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}>
            <Box sx={{ p: 1, bgcolor: `${modulo.colorHex}22`, borderRadius: 1.5, display: 'flex' }}>
              <Ico name={seccion.icono} sx={{ fontSize: 20, color: modulo.colorHex }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{seccion.titulo}</Typography>
              <Typography variant="caption" color="text.secondary">{seccion.pasos.length} pasos</Typography>
            </Box>
            {expandedSec === si && (
              <LinearProgress variant="determinate" value={100}
                sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
                      '& .MuiLinearProgress-bar': { bgcolor: modulo.colorHex } }} />
            )}
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 2.5, pb: 2 }}>
            {seccion.pasos.map((paso, pi) => (
              <React.Fragment key={pi}>
                <PasoItem paso={paso} colorHex={modulo.colorHex} />
                {pi < seccion.pasos.length - 1 && <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />}
              </React.Fragment>
            ))}

            {/* Footer de sección completada */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, p: 1.5,
                       bgcolor: `${modulo.colorHex}11`, borderRadius: 2 }}>
              <CheckCircleIcon sx={{ color: modulo.colorHex, fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Sección completada — {seccion.pasos.length} pasos revisados
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

// ─── Card de módulo (índice) ──────────────────────────────────
function ModuloCard({ modulo, onSelect }) {
  const pasos = totalPasos(modulo);
  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%',
      transition: 'all .2s', cursor: 'pointer',
      '&:hover': { borderColor: modulo.colorHex, boxShadow: `0 4px 20px ${modulo.colorHex}33`, transform: 'translateY(-2px)' },
    }}>
      <CardActionArea onClick={() => onSelect(modulo)} sx={{ height: '100%', p: 0 }}>
        <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header de card */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Avatar sx={{ bgcolor: `${modulo.colorHex}22`, width: 48, height: 48 }}>
              <Ico name={modulo.icono} sx={{ color: modulo.colorHex, fontSize: 24 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{modulo.titulo}</Typography>
              {modulo.soloAdmin && (
                <Chip label="Solo Admin" size="small" color="warning" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
              )}
            </Box>
          </Box>

          {/* Descripción */}
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 2, lineHeight: 1.5 }}>
            {modulo.descripcion}
          </Typography>

          {/* Footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={0.75}>
              <Chip label={`${modulo.secciones.length} temas`} size="small"
                sx={{ bgcolor: `${modulo.colorHex}15`, color: modulo.colorHex, fontWeight: 600, fontSize: 11 }} />
              <Chip label={`${pasos} pasos`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            </Stack>
            <Typography variant="caption" color={modulo.colorHex} fontWeight={700}>
              Ver guía →
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────
export default function ManualPage() {
  const [busqueda, setBusqueda]         = useState('');
  const [categoria, setCategoria]       = useState('todas');
  const [moduloActivo, setModuloActivo] = useState(null);

  const modulosFiltrados = useMemo(() => {
    return MODULOS.filter(m => {
      const catOk = categoria === 'todas' || m.categoria === categoria;
      const searchOk = matchSearch(m, busqueda);
      return catOk && searchOk;
    });
  }, [busqueda, categoria]);

  const totalTutoriales = MODULOS.reduce((a, m) => a + m.secciones.length, 0);
  const totalPasosAll   = MODULOS.reduce((a, m) => a + totalPasos(m), 0);

  // ── Vista: Detalle de módulo ──
  if (moduloActivo) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 900, mx: 'auto' }}>
        <ModuloDetalle modulo={moduloActivo} onBack={() => setModuloActivo(null)} />
      </Box>
    );
  }

  // ── Vista: Índice general ──
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* ── Hero ── */}
      <Paper elevation={0} sx={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #0d47a1 100%)',
        borderRadius: 4, p: { xs: 3, md: 5 }, mb: 4, color: 'white', position: 'relative', overflow: 'hidden',
      }}>
        {/* Decoración */}
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200,
                   borderRadius: '50%', bgcolor: 'rgba(255,255,255,.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -60, right: 80, width: 150, height: 150,
                   borderRadius: '50%', bgcolor: 'rgba(255,255,255,.04)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,.15)', borderRadius: 2.5, display: 'flex' }}>
              <MenuBookIcon sx={{ fontSize: 36 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800}>Manual del Sistema</Typography>
              <Typography variant="body1" sx={{ opacity: 0.85 }}>
                InsightSoft ERP · Guía completa para el usuario
              </Typography>
            </Box>
          </Box>

          {/* Stats */}
          <Stack direction="row" spacing={3} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
            {[
              { label: 'Módulos', value: MODULOS.length },
              { label: 'Tutoriales', value: totalTutoriales },
              { label: 'Pasos documentados', value: totalPasosAll },
              { label: 'Versión', value: MANUAL_VERSION },
            ].map(s => (
              <Box key={s.label} sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800}>{s.value}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>{s.label}</Typography>
              </Box>
            ))}
          </Stack>

          {/* Buscador */}
          <TextField
            fullWidth placeholder="Buscar en el manual... (ej: 'emitir factura', 'anular', 'caja chica', 'arqueo'...)"
            value={busqueda} onChange={e => { setBusqueda(e.target.value); setCategoria('todas'); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,.7)' }} /></InputAdornment>,
              sx: {
                bgcolor: 'rgba(255,255,255,.15)', backdropFilter: 'blur(10px)',
                borderRadius: 3, color: 'white', fontSize: 16,
                '& fieldset': { border: '1.5px solid rgba(255,255,255,.3)' },
                '&:hover fieldset': { border: '1.5px solid rgba(255,255,255,.6) !important' },
                '& input': { color: 'white' },
                '& input::placeholder': { color: 'rgba(255,255,255,.6)', opacity: 1 },
              },
            }}
          />

          <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.6 }}>
            Actualizado: {MANUAL_FECHA} · {modulosFiltrados.length} módulos disponibles
          </Typography>
        </Box>
      </Paper>

      {/* ── Filtros por categoría ── */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {CATEGORIAS.map(cat => (
          <Chip key={cat.id} label={cat.label}
            onClick={() => { setCategoria(cat.id); setBusqueda(''); }}
            color={categoria === cat.id ? 'primary' : 'default'}
            variant={categoria === cat.id ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, cursor: 'pointer' }} />
        ))}
      </Box>

      {/* ── Resultado búsqueda ── */}
      {busqueda && (
        <Alert severity="info" icon={<SearchIcon />} sx={{ mb: 2 }}
          onClose={() => setBusqueda('')}>
          {modulosFiltrados.length > 0
            ? `${modulosFiltrados.length} módulo(s) encontrado(s) para "${busqueda}"`
            : `Sin resultados para "${busqueda}"`}
        </Alert>
      )}

      {/* ── Grid de módulos ── */}
      {modulosFiltrados.length === 0 ? (
        <Paper elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 6, textAlign: 'center' }}>
          <MenuBookIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">No se encontraron módulos para "{busqueda}"</Typography>
          <Chip label="Ver todos" onClick={() => { setBusqueda(''); setCategoria('todas'); }}
            color="primary" sx={{ mt: 2, cursor: 'pointer' }} />
        </Paper>
      ) : (
        <>
          {/* Agrupar por categoría si no hay búsqueda */}
          {!busqueda && categoria === 'todas' ? (
            CATEGORIAS.filter(c => c.id !== 'todas').map(cat => {
              const mods = modulosFiltrados.filter(m => m.categoria === cat.id);
              if (mods.length === 0) return null;
              return (
                <Box key={cat.id} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Chip label={cat.label} variant="outlined" size="small"
                      sx={{ fontWeight: 700, px: 1, borderRadius: 2 }} />
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  <Grid container spacing={2}>
                    {mods.map(m => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                        <ModuloCard modulo={m} onSelect={setModuloActivo} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })
          ) : (
            <Grid container spacing={2}>
              {modulosFiltrados.map(m => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                  <ModuloCard modulo={m} onSelect={setModuloActivo} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* ── Footer ── */}
      <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <MenuBookIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            InsightSoft ERP · Manual del Sistema v{MANUAL_VERSION}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled">
          ¿Tienes dudas? Contacta al administrador del sistema · insight.soft.cpe@gmail.com
        </Typography>
      </Box>
    </Box>
  );
}
